import React, { useEffect, useState } from 'react';
import { db } from '../../../utils/firebase';
import { collection, query, orderBy, getDocs, doc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { EmailCampaign, EmailTemplate } from '../../../types';
import { Plus, Send, Clock, CheckCircle, XCircle, Trash2, Eye, Search, Filter, ChevronDown, AlertCircle } from 'lucide-react';
import CampaignComposer from './CampaignComposer';

const CampaignManager: React.FC = () => {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isComposing, setIsComposing] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<EmailCampaign | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch campaigns
      const campaignsQuery = query(collection(db, 'emailCampaigns'), orderBy('createdAt', 'desc'));
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignList = campaignsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailCampaign[];
      setCampaigns(campaignList);

      // Fetch templates for the composer
      const templatesQuery = query(collection(db, 'emailTemplates'), orderBy('name'));
      const templatesSnapshot = await getDocs(templatesQuery);
      const templateList = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailTemplate[];
      setTemplates(templateList.filter(t => t.isActive));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCampaign(null);
    setIsComposing(true);
  };

  const handleEdit = (campaign: EmailCampaign) => {
    if (campaign.status === 'sent' || campaign.status === 'sending') {
      alert("Cannot edit a campaign that has been sent or is sending.");
      return;
    }
    setEditingCampaign(campaign);
    setIsComposing(true);
  };

  const handleDelete = async (id: string) => {
    const campaign = campaigns.find(c => c.id === id);
    if (campaign?.status === 'sent' || campaign?.status === 'sending') {
      alert("Cannot delete a campaign that has been sent or is sending.");
      return;
    }
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await deleteDoc(doc(db, 'emailCampaigns', id));
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error("Error deleting campaign:", error);
      alert("Failed to delete campaign");
    }
  };

  const handleCancel = async (id: string) => {
    const campaign = campaigns.find(c => c.id === id);
    if (campaign?.status !== 'scheduled') {
      alert("Can only cancel scheduled campaigns.");
      return;
    }
    if (!confirm('Are you sure you want to cancel this scheduled campaign?')) return;
    try {
      await updateDoc(doc(db, 'emailCampaigns', id), {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });
      setCampaigns(prev => prev.map(c =>
        c.id === id ? { ...c, status: 'cancelled' as const } : c
      ));
    } catch (error) {
      console.error("Error cancelling campaign:", error);
      alert("Failed to cancel campaign");
    }
  };

  const handleComposerClose = () => {
    setIsComposing(false);
    setEditingCampaign(null);
    fetchData(); // Refresh list
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <AlertCircle size={16} className="text-gray-400" />;
      case 'scheduled': return <Clock size={16} className="text-blue-500" />;
      case 'sending': return <Send size={16} className="text-yellow-500 animate-pulse" />;
      case 'sent': return <CheckCircle size={16} className="text-green-500" />;
      case 'cancelled': return <XCircle size={16} className="text-red-500" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'sending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'sent': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isComposing) {
    return (
      <CampaignComposer
        campaign={editingCampaign}
        templates={templates}
        onClose={handleComposerClose}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm w-64"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sending">Sending</option>
              <option value="sent">Sent</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={templates.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={templates.length === 0 ? "Create a template first" : "Create new campaign"}
        >
          <Plus size={16} />
          New Campaign
        </button>
      </div>

      {templates.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm">
          <p className="text-yellow-800 text-sm">
            <strong>No active templates.</strong> Create an email template first before creating a campaign.
          </p>
        </div>
      )}

      {/* Campaigns Table */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Campaign</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Status</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Recipients</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Stats</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Created</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredCampaigns.map(campaign => (
              <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                <td className="p-4">
                  <div>
                    <p className="font-bold text-sm">{campaign.name}</p>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{campaign.subject}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(campaign.status)} uppercase tracking-wide`}>
                    {getStatusIcon(campaign.status)}
                    {campaign.status}
                  </span>
                  {campaign.status === 'scheduled' && campaign.scheduledAt && (
                    <p className="text-xs text-gray-500 mt-1">{formatDate(campaign.scheduledAt)}</p>
                  )}
                </td>
                <td className="p-4 text-sm">
                  {campaign.recipientFilter?.type === 'all' ? 'All Subscribers' :
                   campaign.recipientFilter?.type === 'segment' ? campaign.recipientFilter.segment :
                   `${campaign.recipientFilter?.customEmails?.length || 0} custom`}
                </td>
                <td className="p-4">
                  {campaign.stats ? (
                    <div className="text-xs space-y-1">
                      <div className="flex gap-4">
                        <span>Sent: <strong>{campaign.stats.sent}</strong></span>
                        <span>Failed: <strong className="text-red-600">{campaign.stats.failed}</strong></span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="p-4 text-sm text-gray-600">
                  {formatDate(campaign.createdAt)}
                </td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="text-xs font-bold uppercase tracking-wider px-3 py-1 border border-gray-300 hover:border-black transition-colors"
                      >
                        Edit
                      </button>
                    )}
                    {campaign.status === 'scheduled' && (
                      <button
                        onClick={() => handleCancel(campaign.id)}
                        className="text-xs font-bold uppercase tracking-wider px-3 py-1 border border-red-300 text-red-600 hover:border-red-500 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                    {(campaign.status === 'draft' || campaign.status === 'cancelled') && (
                      <button
                        onClick={() => handleDelete(campaign.id)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredCampaigns.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center text-gray-500">
                  {campaigns.length === 0 ? "No campaigns yet. Create your first email campaign." : "No campaigns match your filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer count */}
      <div className="text-sm text-gray-500">
        Showing {filteredCampaigns.length} of {campaigns.length} campaigns
      </div>
    </div>
  );
};

export default CampaignManager;
